import { useState, useEffect, useCallback, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import useModalBack from '../../hooks/useModalBack';

const DRAG_RESISTANCE = 0.6;
const DRAG_DISMISS_THRESHOLD = 80;

export default function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger'
}) {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  // Back button closes modal on mobile
  useModalBack(isOpen, onClose);

  // Drag-to-dismiss state
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(null);
  const sheetRef = useRef(null);

  // Sync visibility with isOpen
  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setClosing(false);
      setDragY(0);
    } else {
      // Parent closed us (e.g., back button) — hide immediately
      setVisible(false);
      setClosing(false);
      setDragY(0);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setVisible(false);
      setDragY(0);
      onClose();
    }, 250);
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setVisible(false);
      setDragY(0);
      onConfirm();
      onClose();
    }, 250);
  }, [onConfirm, onClose]);

  const handleEscape = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleEscape]);

  // Touch handlers for drag-to-dismiss
  const handleTouchStart = useCallback((e) => {
    dragStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (dragStartY.current === null) return;
    const diff = e.touches[0].clientY - dragStartY.current;
    // Only track downward drag
    if (diff > 0) {
      setDragY(diff * DRAG_RESISTANCE);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (dragY > DRAG_DISMISS_THRESHOLD) {
      handleClose();
    } else {
      setDragY(0);
    }
    dragStartY.current = null;
  }, [dragY, handleClose]);

  if (!visible) return null;

  const confirmButtonStyles = {
    danger: 'bg-expense-600 hover:bg-expense-700 text-white',
    warning: 'bg-amber-600 hover:bg-amber-700 text-white',
    primary: 'bg-[var(--accent-bg)] hover:opacity-90 text-[var(--accent-text)]'
  };

  const iconBgStyles = {
    danger: 'bg-expense-100',
    warning: 'bg-amber-100',
    primary: 'bg-surface-100'
  };

  const iconTextStyles = {
    danger: 'text-expense-600',
    warning: 'text-amber-600',
    primary: 'text-surface-600'
  };

  // Drag handle visual feedback
  const atThreshold = dragY > DRAG_DISMISS_THRESHOLD;
  const handleWidth = atThreshold ? 48 : 40;
  const handleOpacity = atThreshold ? 0.7 : 0.3;

  // Backdrop opacity fades as user drags
  const backdropOpacity = closing ? 0 : Math.max(0, 1 - dragY / 200);

  return (
    <>
      {/* Desktop */}
      <div
        className={`hidden xl:flex modal-overlay z-[60] transition-opacity duration-250 ${closing ? 'opacity-0' : 'opacity-100'}`}
        onClick={handleClose}
      >
        <div
          className={`modal-content max-w-md transition-all duration-250 ${closing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-full flex-shrink-0 ${iconBgStyles[variant]}`}>
                <AlertTriangle className={`w-6 h-6 ${iconTextStyles[variant]}`} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-surface-900 mb-2">
                  {title}
                </h3>
                <p className="text-surface-600">
                  {message}
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6 justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2.5 border-2 border-surface-200 text-surface-700 hover:bg-surface-50 rounded-xl font-medium transition-all"
              >
                {cancelText}
              </button>
              <button
                onClick={handleConfirm}
                className={`px-4 py-2.5 rounded-xl font-medium transition-all ${confirmButtonStyles[variant]}`}
              >
                {confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: Bottom sheet with drag-to-dismiss */}
      <div
        className={`xl:hidden fixed inset-0 z-[60] flex items-end transition-all duration-250`}
        style={{
          backgroundColor: `rgba(15, 23, 42, ${backdropOpacity * 0.6})`,
          backdropFilter: closing ? 'none' : 'blur(4px)',
        }}
        onClick={handleClose}
      >
        <div
          ref={sheetRef}
          data-modal="confirm"
          className={`w-full bg-white rounded-t-3xl safe-area-bottom ${
            closing ? 'translate-y-full' : 'animate-slide-up'
          }`}
          style={{
            transform: closing
              ? 'translateY(100%)'
              : `translateY(${dragY}px)`,
            transition: dragStartY.current !== null
              ? 'none'
              : 'transform 250ms ease-out',
          }}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div
              className="h-1 bg-surface-300 rounded-full"
              style={{
                width: handleWidth,
                opacity: handleOpacity,
                transition: 'width 150ms ease, opacity 150ms ease',
              }}
            />
          </div>

          <div className="px-6 pt-4 pb-8" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))' }}>
            {/* Icon + Title centered */}
            <div className="flex flex-col items-center text-center mb-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3 ${iconBgStyles[variant]}`}>
                <AlertTriangle className={`w-7 h-7 ${iconTextStyles[variant]}`} />
              </div>
              <h3 className="text-lg font-bold text-surface-900">
                {title}
              </h3>
              <p className="text-sm text-surface-500 mt-1.5 leading-relaxed max-w-[280px]">
                {message}
              </p>
            </div>

            {/* Buttons: side by side, stack on very small screens */}
            <div className="flex flex-col min-[340px]:flex-row gap-2.5 mt-5">
              <button
                onClick={handleClose}
                className="flex-1 py-3.5 rounded-2xl font-semibold text-[15px] text-surface-600 bg-surface-100 hover:bg-surface-200 transition-all active:scale-[0.98]"
              >
                {cancelText}
              </button>
              <button
                onClick={handleConfirm}
                className={`flex-1 py-3.5 rounded-2xl font-semibold text-[15px] transition-all active:scale-[0.98] ${confirmButtonStyles[variant]}`}
              >
                {confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
